import { Modal, Button, Form } from 'react-bootstrap';
import { Formik, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import Select from 'react-select';
import Avatar from '../Avatar';
import { customUserFilter } from '../../helpers';

const EditTeamGroup = ({
  selectedTeam,
  showEditTeam,
  handleEditTeamDisplay,
  handleUpdateTeam,
}) => {
  // Select option component for team members
  const SelectOption = ({ data }) => (
    <>
      <div className="avatar avatar-xs me-3">
        <Avatar
          firstName={data.firstName}
          lastName={data.lastName}
          imgPath={data.avatar ? data.avatar : ''}
        />
      </div>
      <span className="member-name">
        {data.lastName ? `${data.firstName} ${data.lastName}` : data.firstName}{' '}
        {data.employeeId && (
          <span className="text-muted">({data.employeeId})</span>
        )}
        {data.status === 'inactive' && (
          <span className="text-muted">{` (Inactive)`}</span>
        )}
      </span>
    </>
  );
  SelectOption.propTypes = {
    data: PropTypes.object,
  };

  // Admins list
  const adminsList =
    selectedTeam.teamMembersList?.length > 0
      ? selectedTeam.teamMembersList
          .filter(
            (member) =>
              member.typeName === 'manager' ||
              member.typeName === 'supervisor' ||
              member.typeName === 'account owner'
          )
          .map((admin) => ({
            value: admin.userId,
            label: `${admin.firstName} ${admin.lastName || ''}`,
            firstName: admin.firstName,
            lastName: admin.lastName || '',
            employeeId: admin.employeeId || '',
            avatar: admin.avatar || '',
            status: admin.status || null,
          }))
      : [];
  const preSelectedAdmins =
    selectedTeam.groupAdmins?.length > 0
      ? selectedTeam.groupAdmins.map((admin) => admin._id)
      : [];
  const preSelectedOptions = adminsList.filter((option) =>
    preSelectedAdmins.includes(option.value)
  );

  // Members list
  const membersList = selectedTeam.teamMembersList
    .filter((teamMember) => teamMember.status !== 'inactive')
    .map((member) => ({
      value: member.userId,
      label: `${member.firstName} ${member.lastName || ''}`,
      firstName: member.firstName,
      lastName: member.lastName || '',
      employeeId: member.employeeId || '',
      avatar: member.avatar || '',
      status: member.status || null,
    }));
  const preSelectedMembers =
    selectedTeam.currentMembers?.length > 0
      ? selectedTeam.currentMembers.map((member) => member._id)
      : [];
  const preSelectedMemberOptions = membersList.filter((option) =>
    preSelectedMembers.includes(option.value)
  );

  const validationSchema = Yup.object().shape({
    teamName: Yup.string().required('Please enter a team name'),
    selectedAdmins: Yup.array()
      .of(
        Yup.object({
          value: Yup.string().required(),
          label: Yup.string().required(),
        })
      )
      .nullable(),
    selectedMembers: Yup.array()
      .of(
        Yup.object({
          value: Yup.string().required(),
          label: Yup.string().required(),
        })
      )
      .nullable()
      .test(
        'no-overlap',
        'Admins and members cannot contain the same person',
        function (members) {
          // eslint-disable-next-line react/no-this-in-sfc
          const { selectedAdmins } = this.parent;
          if (!members || !selectedAdmins) return true;

          const adminIds = selectedAdmins.map((a) => a.value);
          const memberIds = members.map((m) => m.value);

          const hasOverlap = memberIds.some((id) => adminIds.includes(id));
          return !hasOverlap;
        }
      ),
  });

  return (
    <Modal
      show={showEditTeam}
      onHide={() => {
        handleEditTeamDisplay(false);
      }}
      centered
    >
      <Modal.Header>
        <Modal.Title className="m-0">Edit Team Group</Modal.Title>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={() => {
            handleEditTeamDisplay(false);
          }}
        />
      </Modal.Header>
      <Formik
        enableReinitialize
        initialValues={{
          teamName: selectedTeam.teamName,
          teamId: selectedTeam.teamId,
          selectedAdmins: preSelectedOptions,
          selectedMembers: preSelectedMemberOptions,
        }}
        validationSchema={validationSchema}
        onSubmit={(values, { resetForm }) => {
          handleUpdateTeam(values);
          resetForm();
        }}
      >
        {(formikprops) => (
          <Form onSubmit={formikprops.handleSubmit}>
            <Modal.Body>
              <section>
                <Form.Group controlId="worksessionType">
                  <Form.Label>Team Name</Form.Label>
                  <Field
                    className="form-control"
                    type="text"
                    placeholder="Enter team name"
                    name="teamName"
                  />
                  <ErrorMessage
                    name="teamName"
                    render={(msg) => (
                      <small className="text-danger">{msg}</small>
                    )}
                  />
                </Form.Group>
                <Form.Group controlId="selectedAdmins">
                  <Form.Label>Team Group Admin(s)</Form.Label>
                  <Select
                    isMulti
                    isClearable={false}
                    name="selectedAdmins"
                    options={adminsList}
                    formatOptionLabel={(data) => <SelectOption data={data} />}
                    filterOption={customUserFilter}
                    onChange={(selected) =>
                      formikprops.setFieldValue('selectedAdmins', selected)
                    }
                    value={formikprops.values.selectedAdmins}
                    placeholder="Select Group Admin(s)"
                    className="site-multi-select"
                    classNamePrefix="select"
                    theme={(theme) => ({
                      ...theme,
                      colors: {
                        ...theme.colors,
                        primary: '#0649b5',
                        neutral10: '#edf2f9',
                        neutral20: '#d2ddec',
                        neutral30: '#d2ddec',
                        neutral50: '#b1c2da',
                        neutral80: '#12263f',
                        dangerLight: '#DEEBFF',
                        danger: '#12263f',
                      },
                    })}
                  />
                  <ErrorMessage
                    name="selectedAdmins"
                    render={(msg) => (
                      <small className="text-danger">{msg}</small>
                    )}
                  />
                </Form.Group>
                <Form.Group controlId="selectedMembers">
                  <Form.Label>Team Member(s)</Form.Label>
                  <Select
                    isMulti
                    isClearable={false}
                    name="selectedMembers"
                    options={membersList}
                    formatOptionLabel={(data) => <SelectOption data={data} />}
                    filterOption={customUserFilter}
                    onChange={(selected) =>
                      formikprops.setFieldValue('selectedMembers', selected)
                    }
                    value={formikprops.values.selectedMembers}
                    placeholder="Select Member(s)"
                    className="site-multi-select"
                    classNamePrefix="select"
                    theme={(theme) => ({
                      ...theme,
                      colors: {
                        ...theme.colors,
                        primary: '#0649b5',
                        neutral10: '#edf2f9',
                        neutral20: '#d2ddec',
                        neutral30: '#d2ddec',
                        neutral50: '#b1c2da',
                        neutral80: '#12263f',
                        dangerLight: '#DEEBFF',
                        danger: '#12263f',
                      },
                    })}
                  />
                  <ErrorMessage
                    name="selectedMembers"
                    render={(msg) => (
                      <small className="text-danger">{msg}</small>
                    )}
                  />
                </Form.Group>
              </section>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
              <Button
                variant="link"
                onClick={() => {
                  handleEditTeamDisplay(false);
                }}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Save
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

EditTeamGroup.propTypes = {
  selectedTeam: PropTypes.object,
  showEditTeam: PropTypes.bool,
  handleEditTeamDisplay: PropTypes.func,
  handleUpdateTeam: PropTypes.func,
};

export default EditTeamGroup;
